import { selectFeatureFlags } from 'core/userContext/selectors';
import isEmpty from 'lodash/isEmpty';
import { getIsWhatsAppCampaign } from 'modules/campaigns/lib/utils';
import { CampaignType, MessageMedium } from 'modules/domains/campaign';
import { useSelector } from 'react-redux';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import { Edit16 } from 'ui/icons';
import { Spinner } from 'ui/spinner';
import { Tooltip } from 'ui/tooltip';
import { CAMPAIGN_STEP_NAMES_MAPPER } from '../../constants';
import { useCampaignContext, useEditorContext, useTemplateContext } from '../../context';
import { CampaignEditorView, CampaignStep } from '../../types';
import { getShouldShowPerformanceAlerts } from '../../utils';
import { getAreAlertUpdatesSupported, getShouldDisableEditCampaignSettings } from '../utils';
import {
  CampaignCardHeader,
  CampaignCardTitle,
  CampaignStepCardHeaderTitle,
  CampaignStepCardWrapper,
  SummaryContentWrapper,
} from './CampaignCard.styled';
import { CampaignSummaryAlerts } from './CampaignSummaryAlerts';
import { getCampaignSummarySections } from './utils';

export const CAMPAIGN_SUMMARY_CONTENT_TEST_ID = 'campaign-summary-content';

const SummarySections = ({
  messageMedium,
  campaignTypeDefinition,
}: {
  campaignTypeDefinition: CampaignType;
  messageMedium: MessageMedium;
}) => {
  const sections = getCampaignSummarySections(campaignTypeDefinition, messageMedium);

  return (
    <>
      {sections?.map((Section: () => JSX.Element) => (
        <Section key={Section.name} />
      ))}
    </>
  );
};

export const CampaignSummaryContent = () => {
  const { setCampaignEditorView, canDraftMessaging } = useEditorContext();
  const { campaign } = useCampaignContext();
  const { templateAndContent, isLoading } = useTemplateContext();
  const { messageMedium, campaignTypeDefinition, campaignState, isJourneyAssociated } = campaign;
  const {
    CampaignPerformanceAlerts: isCampaignPerformanceAlertsEnabled,
    JourneysCampaignPerformanceAlerts: isJourneysCampaignPerformanceAlertsEnabled,
  } = useSelector(selectFeatureFlags);
  const isWhatsAppCampaign = getIsWhatsAppCampaign(messageMedium);

  const shouldDisableEditButton = getShouldDisableEditCampaignSettings(campaignState);
  const isNotEditable = !canDraftMessaging || shouldDisableEditButton;

  /* Alerts UI is only shown if the FF is on */
  const showPerformanceAlertsFeature = getShouldShowPerformanceAlerts({
    campaignType: campaignTypeDefinition,
    isCampaignPerformanceAlertsEnabled,
    isJourneysCampaignPerformanceAlertsEnabled,
    isJourneyAssociated,
    isWhatsAppCampaign,
  });

  const areAlertUpdatesSupported = getAreAlertUpdatesSupported(
    campaignTypeDefinition,
    campaignState,
  );

  const shouldShowAlertsToggle = showPerformanceAlertsFeature && areAlertUpdatesSupported;

  const tooltipContent = !canDraftMessaging
    ? 'You do not have permission to edit campaigns'
    : `${campaignState} campaign settings cannot be edited.`;

  return (
    <CampaignStepCardWrapper data-test={CAMPAIGN_SUMMARY_CONTENT_TEST_ID}>
      {shouldShowAlertsToggle && <CampaignSummaryAlerts />}
      <CampaignCardHeader>
        <CampaignStepCardHeaderTitle>
          <CampaignCardTitle>{CAMPAIGN_STEP_NAMES_MAPPER[CampaignStep.Summary]}</CampaignCardTitle>
        </CampaignStepCardHeaderTitle>

        <Tooltip isEnabled={isNotEditable} tooltipContent={tooltipContent}>
          <Button
            isDisabled={isNotEditable}
            data-test="edit-button"
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.SECONDARY}
            onClick={() => setCampaignEditorView(CampaignEditorView.Edit)}
          >
            <Edit16 />
            Edit campaign
          </Button>
        </Tooltip>
      </CampaignCardHeader>
      {isLoading || isEmpty(templateAndContent) ? (
        <SummaryContentWrapper>
          <Spinner />
        </SummaryContentWrapper>
      ) : (
        <SummarySections
          messageMedium={messageMedium}
          campaignTypeDefinition={campaignTypeDefinition}
        />
      )}
    </CampaignStepCardWrapper>
  );
};
